// @flow
import { Record } from "immutable"
import jsCookie from "js-cookie"
import { createSelector } from "reselect"

import type User from "../../records/User"
import cuckoo from "../cuckoo/cuckoo"
import * as auth from "../../../../app/scripts/tools/authApi"
import socialApi from "./socialAuthApi"
import {
  bidSelector,
  bookingDataSelector,
} from "../../../mmb/scenes/AccountBooking/components/Booking/services/selector"
import type { Provider } from "./socialAuthApi"
import type { UserStateInst, Action, GetState, FormType } from "./types"
import { cuckooEvents } from "../../consts/enums"
import apiBookingTokens from "../../../mmb/services/apiBookingTokens/apiBookingTokens"
import { UA_SESSION_TOKEN, KW_AUTH_TOKEN } from "../../consts/auth"

const RESET = "user/RESET"
const RESET_ERROR = "user/RESET_ERROR"
const SIGN_UP = "user/SIGN_UP"
const SIGN_UP_REQUEST = "user/SIGN_UP_REQUEST"
const SIGN_UP_SUCCESS = "user/SIGN_UP_SUCCESS"
const SIGN_UP_FAILURE = "user/SIGN_UP_FAILURE"
const LOGIN = "user/LOGIN"
const LOGIN_REQUEST = "user/LOGIN_REQUEST"
export const LOGIN_SUCCESS = "user/LOGIN_SUCCESS"
const LOGIN_FAILURE = "user/LOGIN_FAILURE"
const MAGIC_TOKEN_FAILURE = "user/MAGIC_TOKEN_FAILURE"
export const LOGOUT = "user/LOGOUT"
export const LOGOUT_REQUEST = "user/LOGOUT_REQUEST"
export const LOGOUT_SUCCESS = "user/LOGOUT_SUCCESS"
export const LOGOUT_FAILURE = "user/LOGOUT_FAILURE"
const IGNORE = "IGNORE"

export type UserReducerType = Record<{
  user: ?User,
  loading: boolean,
  error: ?string,
  magicTokenFailed: boolean,
}>

const UserDefaultState = Record({
  user: null,
  loading: false,
  error: null,
  magicTokenFailed: false,
})

export default function UserReducer(state: UserStateInst = new UserDefaultState(), action: Action) {
  if (!(state instanceof UserDefaultState)) return new UserDefaultState(state)

  switch (action.type) {
    case RESET:
      return new UserDefaultState()
    case RESET_ERROR:
      return state.set("error", null)
    // ---------- SIGN_UP ----------
    case SIGN_UP_REQUEST:
      return state
        .set("user", null)
        .set("loading", true)
        .set("error", null)

    case SIGN_UP_FAILURE:
      return state
        .set("user", null)
        .set("loading", false)
        .set("error", action.error)

    case SIGN_UP_SUCCESS:
      return state
        .set("user", null)
        .set("loading", false)
        .set("error", null)

    // ---------- LOGIN ----------
    case LOGIN_REQUEST:
      return state
        .set("user", null)
        .set("loading", true)
        .set("error", null)
        .set("magicTokenFailed", false)

    case LOGIN_FAILURE:
      return state
        .set("user", null)
        .set("loading", false)
        .set("error", action.error)

    case LOGIN_SUCCESS:
      return state
        .set("user", action.res)
        .set("loading", false)
        .set("error", null)
        .set("magicTokenFailed", false)

    case MAGIC_TOKEN_FAILURE:
      return state
        .set("user", null)
        .set("loading", false)
        .set("magicTokenFailed", true)

    // ---------- LOGOUT ----------
    case LOGOUT_REQUEST:
      return state
        .set("user", null)
        .set("loading", true)
        .set("error", null)

    case LOGOUT_FAILURE:
      return state
        .set("user", null)
        .set("loading", false)
        .set("error", "unknown error") // TODO

    case LOGOUT_SUCCESS:
      return state
        .set("user", null)
        .set("loading", false)
        .set("error", null)

    default:
      return state
  }
}

export const socialAuthNitro = (provider: Provider) => async (dispatch: Dispatch<*>) => {
  dispatch({ type: LOGIN_REQUEST, _track: true })

  const body = await socialApi(provider)

  dispatch({ type: LOGIN_SUCCESS, body, _track: true })
  if (body.authorization_url) {
    window.location = body.authorization_url
  }

  cuckoo.infinario("userLogin", { loginWith: provider })
}

export const socialAuth = (provider: Provider) => async (dispatch: Dispatch<*>) => {
  try {
    const res = await dispatch(socialAuthNitro(provider))

    return res
  } catch (error) {
    return dispatch({ type: LOGIN_FAILURE, error, _track: true })
  }
}

export const signup = (form: FormType) => ({
  type: SIGN_UP,
  promise: auth.signup(form),
})

export const login = (form: FormType) => ({
  type: LOGIN,
  promise: auth.login(form.update("email", email => email.trim())), // strip email whitespace
})

export const logout = () => (dispatch: Dispatch<*>, getState: GetState) => {
  const state = getState()
  const { user } = state.user
  const booking = bookingDataSelector(state)
  const bid = bidSelector(state)
  let promise

  if (user) {
    promise = auth.logout(bid)
  } else if (booking) {
    promise = new Promise(resolve => {
      cuckoo.track(cuckooEvents.AUTH_LOGOUT)
      cuckoo.infinario("userSimpleTokenLogout")
      apiBookingTokens.remove(bid)
      jsCookie.remove(`simple_token-${bid}`)
      resolve()
    })
  }

  dispatch({
    type: LOGOUT,
    promise,
  })
}

export const resetError = () => ({
  type: RESET_ERROR,
})

// BEWARE
// side effects as fuck
export const init = () => {
  // only do this in the browser
  if (typeof window === "undefined") {
    return {
      type: IGNORE,
    }
  }

  const notOauth = !window.location.pathname.match(/\/..\/oauth-login/)

  if (notOauth && jsCookie.get(UA_SESSION_TOKEN)) {
    return {
      type: LOGIN,
      promise: auth.loginWithCookie(),
    }
  }

  const kwAuthToken = jsCookie.get(KW_AUTH_TOKEN)

  if (notOauth && kwAuthToken) {
    return (dispatch: Dispatch<*>, getState: GetState) => {
      dispatch({ type: LOGIN_REQUEST })
      auth
        .processKWToken(getState(), kwAuthToken)
        .then(user => dispatch({ type: LOGIN_SUCCESS, res: user }))
        .catch(() => dispatch({ type: MAGIC_TOKEN_FAILURE }))
    }
  }

  return {
    type: IGNORE,
  }
}

export const userSelector = (state: UserStateInst) => state.user.user

export const getUserAffilId: $FlowFixMe = createSelector(
  userSelector,
  user => user && user.affiliateId,
)
